import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, DollarSign, AlertTriangle as AlertTriangleIcon, FileText, ExternalLink, Printer, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { nonConformityService, ncCostService, attachmentService, debitNoteService, clientService, supplierService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FileUploadZone } from '../components/FileUploadZone';
import { PrintableNC, PrintableDebitNoteSection, PrintableDossier, PrintableNCRResponse } from '../components/PrintableDocuments';
import { useReactToPrint } from 'react-to-print';

const COST_CATEGORIES = ['scrap', 'rework', 'machine_downtime', 'transport', 'customer_charge', 'sorting', 'other'];
const STATUSES = ['open', 'under_analysis', 'waiting', 'closed'];

export default function NCDetailPage() {
  const { id } = useParams();
  const [nc, setNc] = useState(null);
  const [costs, setCosts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { hasRole } = useAuth();

  const [costForm, setCostForm] = useState({
    cost_category: 'scrap',
    cost_description: '',
    quantity: '',
    unit_cost: '',
    total_cost: '',
    notes: ''
  });

  const [newStatus, setNewStatus] = useState('');
  const [editForm, setEditForm] = useState({});
  const [dispositionForm, setDispositionForm] = useState({
    total_nonconforming_quantity: '',
    scrapped_quantity: '',
    internal_scrap_quantity: '',
    customer_concession_quantity: '',
    reworked_quantity: '',
  });
  const [dispositionEditing, setDispositionEditing] = useState(false);

  // Debit Notes
  const [debitNotes, setDebitNotes] = useState([]);
  const [dnDialogOpen, setDnDialogOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const nav = useNavigate();
  const [dnForm, setDnForm] = useState({
    recipient_type: 'customer',
    customer_id: '',
    supplier_id: '',
    subject: '',
    description: '',
    notes: '',
  });

  // Esito Contestazione (NC Ricevute)
  const [contestazioneEditing, setContestazioneEditing] = useState(false);
  const [contestazioneForm, setContestazioneForm] = useState({
    technical_outcome: '', outcome_reason: '', response_notes: '', response_date: '',
  });

  // Gestione Economica (NC Ricevute)
  const [economicaEditing, setEconomicaEditing] = useState(false);
  const [economicaForm, setEconomicaForm] = useState({
    requested_charge_amount: '', recognized_charge_amount: '', economic_outcome: '', economic_notes: '',
  });

  // Print refs
  const printNCRef = useRef(null);
  const printDNRef = useRef(null);
  const printDossierRef = useRef(null);
  const printNCRResponseRef = useRef(null);
  const [printDnData, setPrintDnData] = useState(null);

  const handlePrintNC = useReactToPrint({ contentRef: printNCRef });
  const handlePrintDN = useReactToPrint({ contentRef: printDNRef });
  const handlePrintDossier = useReactToPrint({ contentRef: printDossierRef });
  const handlePrintNCRResponse = useReactToPrint({ contentRef: printNCRResponseRef });

  const doPrintNC = () => handlePrintNC();

  const doPrintDN = async () => {
    if (debitNotes.length === 0) { toast.error('Nessuna nota di debito collegata'); return; }
    try {
      const res = await debitNoteService.getPrintData(debitNotes[0].id);
      setPrintDnData(res.data);
      setTimeout(() => handlePrintDN(), 300);
    } catch { toast.error('Errore caricamento dati stampa'); }
  };

  const doPrintDossier = async () => {
    if (debitNotes.length > 0) {
      try {
        const res = await debitNoteService.getPrintData(debitNotes[0].id);
        setPrintDnData(res.data);
      } catch { setPrintDnData(null); }
    } else {
      setPrintDnData(null);
    }
    setTimeout(() => handlePrintDossier(), 300);
  };

  // Server-side PDF download for unified dossier (NC Emesse or any category)
  const doDownloadPdf = async (mode) => {
    const labels = { nc_only: 'Non Conformità', dossier: 'Fascicolo Completo', attachments_only: 'Allegati' };
    toast.info(`Generazione ${labels[mode] || 'PDF'}...`);
    try {
      const res = await nonConformityService.generatePdf(id, mode);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ncNum = nc?.nc_number || 'NC';
      const suffix = mode === 'dossier' ? '_Fascicolo_Completo' : mode === 'attachments_only' ? '_Allegati' : '';
      link.download = `${ncNum}${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${labels[mode]} generato con successo`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione PDF');
    }
  };

  const loadNC = useCallback(async () => {
    try {
      const [ncRes, costsRes, attachmentsRes] = await Promise.all([
        nonConformityService.getById(id),
        ncCostService.getAll({ non_conformity_id: id }),
        attachmentService.getAll({ entity_type: 'nc', entity_id: id })
      ]);
      setNc(ncRes.data);
      setCosts(costsRes.data);
      setAttachments(attachmentsRes.data);
      setEditForm({
        containment_action: ncRes.data.containment_action || '',
        corrective_action: ncRes.data.corrective_action || '',
        responsible_person: ncRes.data.responsible_person || '',
        due_date: ncRes.data.due_date || '',
        notes: ncRes.data.notes || ''
      });
      setDispositionForm({
        total_nonconforming_quantity: ncRes.data.total_nonconforming_quantity ?? '',
        scrapped_quantity: ncRes.data.scrapped_quantity ?? '',
        internal_scrap_quantity: ncRes.data.internal_scrap_quantity ?? '',
        customer_concession_quantity: ncRes.data.customer_concession_quantity ?? '',
        reworked_quantity: ncRes.data.reworked_quantity ?? '',
      });
      setContestazioneForm({
        technical_outcome: ncRes.data.technical_outcome || '',
        outcome_reason: ncRes.data.outcome_reason || '',
        response_notes: ncRes.data.response_notes || '',
        response_date: ncRes.data.response_date || '',
      });
      setEconomicaForm({
        requested_charge_amount: ncRes.data.requested_charge_amount ?? '',
        recognized_charge_amount: ncRes.data.recognized_charge_amount ?? '',
        economic_outcome: ncRes.data.economic_outcome || '',
        economic_notes: ncRes.data.economic_notes || '',
      });
    } catch (error) {
      toast.error('Errore nel caricamento dettagli NC');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAttachments = useCallback(async () => {
    try {
      const res = await attachmentService.getAll({ entity_type: 'nc', entity_id: id });
      setAttachments(res.data);
    } catch { /* allegati opzionali */ }
  }, [id]);

  const loadDebitNotes = useCallback(async () => {
    try {
      const res = await debitNoteService.getAll({ non_conformity_id: id });
      setDebitNotes(res.data);
    } catch { /* nessuna nota di debito */ }
  }, [id]);

  useEffect(() => {
    loadNC();
    loadDebitNotes();
  }, [loadNC, loadDebitNotes]);

  const openDebitNoteDialog = async () => {
    try {
      const [cliRes, supRes] = await Promise.allSettled([clientService.getAll(), supplierService.getAll()]);
      if (cliRes.status === 'fulfilled') setClients(cliRes.value.data.filter(c => c.is_active !== false));
      if (supRes.status === 'fulfilled') setSuppliers(supRes.value.data);
    } catch { /* clienti/fornitori opzionali per select */ }
    const subject = nc ? `Nota di Debito - NC ${nc.nc_number}` : 'Nota di Debito';
    setDnForm({ recipient_type: 'customer', customer_id: nc?.client_id || '', supplier_id: nc?.supplier_id || '', subject, description: nc?.defect_description || '', notes: '' });
    setDnDialogOpen(true);
  };

  const handleCreateDebitNote = async (e) => {
    e.preventDefault();
    if (!dnForm.subject) { toast.error('Compilare l\'oggetto'); return; }
    if (dnForm.recipient_type === 'customer' && !dnForm.customer_id) { toast.error('Selezionare un cliente'); return; }
    if (dnForm.recipient_type === 'supplier' && !dnForm.supplier_id) { toast.error('Selezionare un fornitore'); return; }
    try {
      const payload = {
        recipient_type: dnForm.recipient_type,
        customer_id: dnForm.recipient_type === 'customer' ? dnForm.customer_id : null,
        supplier_id: dnForm.recipient_type === 'supplier' ? dnForm.supplier_id : null,
        non_conformity_id: id,
        subject: dnForm.subject,
        description: dnForm.description,
        notes: dnForm.notes,
      };
      const res = await debitNoteService.create(payload);
      toast.success(`Nota di debito ${res.data.debit_note_number} creata`);
      setDnDialogOpen(false);
      nav(`/debit-notes/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione nota di debito');
    }
  };

  const handleAddCost = async (e) => {
    e.preventDefault();
    try {
      await ncCostService.create({
        non_conformity_id: id,
        cost_category: costForm.cost_category,
        cost_description: costForm.cost_description,
        quantity: costForm.quantity ? parseInt(costForm.quantity) : null,
        unit_cost: costForm.unit_cost ? parseFloat(costForm.unit_cost) : null,
        total_cost: parseFloat(costForm.total_cost),
        notes: costForm.notes || null
      });
      toast.success('Costo aggiunto');
      setCostDialogOpen(false);
      setCostForm({ cost_category: 'scrap', cost_description: '', quantity: '', unit_cost: '', total_cost: '', notes: '' });
      loadNC();
    } catch (error) {
      toast.error('Errore nell\'aggiunta costo');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (window.confirm('Delete this cost entry?')) {
      try {
        await ncCostService.delete(costId);
        toast.success('Costo eliminato');
        loadNC();
      } catch (error) {
        toast.error('Errore nell\'eliminazione costo');
      }
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await nonConformityService.update(id, { status: newStatus });
      toast.success('Status updated');
      setStatusDialogOpen(false);
      loadNC();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateNC = async (e) => {
    e.preventDefault();
    try {
      await nonConformityService.update(id, editForm);
      toast.success('NC updated');
      setEditDialogOpen(false);
      loadNC();
    } catch (error) {
      toast.error('Failed to update NC');
    }
  };

  const calculateTotalFromQty = () => {
    const qty = parseFloat(costForm.quantity) || 0;
    const unit = parseFloat(costForm.unit_cost) || 0;
    if (qty && unit) {
      setCostForm({ ...costForm, total_cost: (qty * unit).toFixed(2) });
    }
  };

  const dispositionSum =
    (parseInt(dispositionForm.scrapped_quantity) || 0) +
    (parseInt(dispositionForm.internal_scrap_quantity) || 0) +
    (parseInt(dispositionForm.customer_concession_quantity) || 0) +
    (parseInt(dispositionForm.reworked_quantity) || 0);
  const dispositionTotal = parseInt(dispositionForm.total_nonconforming_quantity) || 0;
  const dispositionMismatch = dispositionTotal > 0 && dispositionSum !== dispositionTotal;

  const handleSaveDisposition = async () => {
    const payload = {};
    ['total_nonconforming_quantity', 'scrapped_quantity', 'internal_scrap_quantity', 'customer_concession_quantity', 'reworked_quantity'].forEach(k => {
      const v = dispositionForm[k];
      payload[k] = v !== '' && v !== null ? parseInt(v) : null;
    });
    try {
      await nonConformityService.update(id, payload);
      toast.success('Disposizione materiale aggiornata');
      setDispositionEditing(false);
      loadNC();
    } catch {
      toast.error('Errore salvataggio disposizione');
    }
  };

  const handleSaveContestazione = async () => {
    try {
      await nonConformityService.update(id, {
        technical_outcome: contestazioneForm.technical_outcome || null,
        outcome_reason: contestazioneForm.outcome_reason || null,
        response_notes: contestazioneForm.response_notes || null,
        response_date: contestazioneForm.response_date || null,
      });
      toast.success('Esito contestazione aggiornato');
      setContestazioneEditing(false);
      loadNC();
    } catch { toast.error('Errore salvataggio esito'); }
  };

  const handleSaveEconomica = async () => {
    try {
      await nonConformityService.update(id, {
        requested_charge_amount: economicaForm.requested_charge_amount !== '' ? parseFloat(economicaForm.requested_charge_amount) : null,
        recognized_charge_amount: economicaForm.recognized_charge_amount !== '' ? parseFloat(economicaForm.recognized_charge_amount) : null,
        economic_outcome: economicaForm.economic_outcome || null,
        economic_notes: economicaForm.economic_notes || null,
      });
      toast.success('Gestione economica aggiornata');
      setEconomicaEditing(false);
      loadNC();
    } catch { toast.error('Errore salvataggio gestione economica'); }
  };

  const getTypeBadge = (type) => {
    const styles = {
      internal: 'bg-indigo-500 text-white',
      supplier: 'bg-orange-500 text-white',
      customer: 'bg-teal-500 text-white',
    };
    return styles[type] || styles.internal;
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      minor: 'bg-sky-500 text-white',
      major: 'bg-orange-500 text-white',
      critical: 'bg-red-600 text-white',
    };
    return styles[severity] || styles.minor;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-red-500 text-white',
      under_analysis: 'bg-amber-500 text-white',
      waiting: 'bg-sky-500 text-white',
      closed: 'bg-emerald-500 text-white',
    };
    return styles[status] || styles.open;
  };

  const totalCost = costs.reduce((sum, c) => sum + (c.total_cost || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!nc) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Non conformity not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24" data-testid="nc-detail-page">
      {/* Hidden print components */}
      <div className="hidden">
        <PrintableNC ref={printNCRef} nc={nc} costs={costs} attachments={attachments} />
        <PrintableDebitNoteSection ref={printDNRef} data={printDnData} />
        <PrintableDossier ref={printDossierRef} nc={nc} costs={costs} attachments={attachments} debitNote={printDnData} />
        {nc.nc_category === 'ricevuta' && <PrintableNCRResponse ref={printNCRResponseRef} nc={nc} attachments={attachments} />}
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to={nc.nc_category === 'ricevuta' ? '/nc-ricevute' : nc.nc_category === 'emessa' ? '/nc-emesse' : '/nc-interne'}>
            <Button variant="ghost" size="sm" className="h-10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 font-mono">{nc.nc_number}</h1>
              <Badge className={nc.nc_category === 'ricevuta' ? 'bg-teal-500 text-white' : nc.nc_category === 'emessa' ? 'bg-orange-500 text-white' : 'bg-indigo-500 text-white'}>
                {nc.nc_category === 'ricevuta' ? 'Ricevuta' : nc.nc_category === 'emessa' ? 'Emessa' : 'Interna'}
              </Badge>
              <Badge className={getSeverityBadge(nc.severity)}>{nc.severity}</Badge>
              <Badge className={getStatusBadge(nc.status)}>{nc.status?.replace('_', ' ')}</Badge>
            </div>
            <p className="text-slate-500 mt-1">Aperta il: {nc.opening_date}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasRole('admin', 'quality') && (
            <>
              <Button variant="outline" className="h-12 px-5 text-base" onClick={() => setEditDialogOpen(true)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Modifica
              </Button>
              <Button variant="outline" className="h-12 px-5 text-base" onClick={() => { setNewStatus(nc.status); setStatusDialogOpen(true); }}>
                Cambia Stato
              </Button>
              <Button className="h-12 px-5 text-base gap-2" onClick={openDebitNoteDialog} data-testid="create-debit-note-btn">
                <FileText className="w-4 h-4" />
                Genera Nota di Debito
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-12 px-5 text-base gap-2" data-testid="print-menu-btn">
                    <Printer className="w-4 h-4" />
                    Stampa
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {nc.nc_category === 'emessa' ? (
                    <>
                      <DropdownMenuItem onClick={doPrintNC} data-testid="print-nc-btn">
                        <FileText className="w-4 h-4 mr-2 text-orange-600" />
                        Stampa Non Conformita
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => doDownloadPdf('dossier')} data-testid="print-dossier-btn">
                        <FileText className="w-4 h-4 mr-2 text-blue-600" />
                        Stampa Fascicolo Completo (PDF)
                      </DropdownMenuItem>
                      {attachments.length > 0 && (
                        <DropdownMenuItem onClick={() => doDownloadPdf('attachments_only')} data-testid="print-attachments-btn">
                          <FileText className="w-4 h-4 mr-2 text-emerald-600" />
                          Stampa Solo Allegati (PDF)
                        </DropdownMenuItem>
                      )}
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={doPrintNC} data-testid="print-nc-btn">
                        <FileText className="w-4 h-4 mr-2 text-red-600" />
                        Stampa Non Conformità
                      </DropdownMenuItem>
                      {nc.nc_category === 'ricevuta' && (
                        <DropdownMenuItem onClick={() => handlePrintNCRResponse()} data-testid="print-ncr-response-btn">
                          <FileText className="w-4 h-4 mr-2 text-teal-600" />
                          Stampa Risposta NC Ricevuta
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={doPrintDN} disabled={debitNotes.length === 0} data-testid="print-dn-btn">
                        <FileText className="w-4 h-4 mr-2 text-slate-600" />
                        Stampa Nota di Debito
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={doPrintDossier} data-testid="print-dossier-btn">
                        <FileText className="w-4 h-4 mr-2 text-blue-600" />
                        Stampa Fascicolo Completo
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Cost Summary */}
      <Card className="bg-rose-50 border-rose-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-rose-600" />
              <div>
                <p className="text-sm text-rose-600 font-bold">Costo Totale NC</p>
                <p className="text-3xl font-bold text-rose-700">€{totalCost.toFixed(2)}</p>
              </div>
            </div>
            {hasRole('admin', 'quality') && (
              <Button className="h-12 px-5 text-base" onClick={() => setCostDialogOpen(true)} data-testid="add-cost-btn">
                <Plus className="w-5 h-5 mr-2" />
                Aggiungi Costo
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* NC Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">NC Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Product</p>
                <p className="font-mono">{nc.product_code || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Lot</p>
                {nc.lot_id ? (
                  <Link to={`/production-lots/${nc.lot_id}`} className="font-mono text-blue-600 hover:underline">
                    {nc.lot_code}
                  </Link>
                ) : '-'}
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Client</p>
                <p>{nc.client_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Supplier</p>
                <p>{nc.supplier_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Macro Area</p>
                <p>{nc.macro_area || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Involved Qty</p>
                <p className="font-medium">{nc.involved_quantity || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Actions & Responsibility</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Responsible Person</p>
              <p>{nc.responsible_person || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Due Date</p>
              <p>{nc.due_date || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Origin</p>
              <p>{nc.origin || '-'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Description */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Defect Description</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-700">{nc.defect_description}</p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Containment Action</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">{nc.containment_action || 'Not defined'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Corrective Action</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">{nc.corrective_action || 'Not defined'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Material Disposition */}
      <Card data-testid="material-disposition-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Disposizione Materiale</CardTitle>
            {hasRole('admin', 'quality') && !dispositionEditing && (
              <Button variant="outline" size="sm" onClick={() => setDispositionEditing(true)} data-testid="edit-disposition-btn">
                <Edit2 className="w-4 h-4 mr-1" />Modifica
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {dispositionEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs">Quantità NC Totale</Label>
                  <Input type="number" min="0" value={dispositionForm.total_nonconforming_quantity}
                    onChange={e => setDispositionForm(f => ({ ...f, total_nonconforming_quantity: e.target.value }))}
                    data-testid="disp-total" />
                </div>
                <div>
                  <Label className="text-xs">Scarto</Label>
                  <Input type="number" min="0" value={dispositionForm.scrapped_quantity}
                    onChange={e => setDispositionForm(f => ({ ...f, scrapped_quantity: e.target.value }))}
                    data-testid="disp-scrapped" />
                </div>
                <div>
                  <Label className="text-xs">Scarto Interno</Label>
                  <Input type="number" min="0" value={dispositionForm.internal_scrap_quantity}
                    onChange={e => setDispositionForm(f => ({ ...f, internal_scrap_quantity: e.target.value }))}
                    data-testid="disp-internal" />
                </div>
                <div>
                  <Label className="text-xs">Concessione Cliente</Label>
                  <Input type="number" min="0" value={dispositionForm.customer_concession_quantity}
                    onChange={e => setDispositionForm(f => ({ ...f, customer_concession_quantity: e.target.value }))}
                    data-testid="disp-concession" />
                </div>
                <div>
                  <Label className="text-xs">Rilavorati</Label>
                  <Input type="number" min="0" value={dispositionForm.reworked_quantity}
                    onChange={e => setDispositionForm(f => ({ ...f, reworked_quantity: e.target.value }))}
                    data-testid="disp-reworked" />
                </div>
              </div>
              {dispositionMismatch && (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm" data-testid="disposition-warning">
                  <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" />
                  <span>Le quantità non coincidono: {dispositionSum} di {dispositionTotal} pezzi assegnati (differenza: {dispositionTotal - dispositionSum})</span>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setDispositionEditing(false); setDispositionForm({ total_nonconforming_quantity: nc.total_nonconforming_quantity ?? '', scrapped_quantity: nc.scrapped_quantity ?? '', internal_scrap_quantity: nc.internal_scrap_quantity ?? '', customer_concession_quantity: nc.customer_concession_quantity ?? '', reworked_quantity: nc.reworked_quantity ?? '' }); }}>Annulla</Button>
                <Button size="sm" onClick={handleSaveDisposition} data-testid="save-disposition-btn">Salva</Button>
              </div>
            </div>
          ) : (
            <div>
              {nc.total_nonconforming_quantity != null ? (
                <div className="space-y-3">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500 uppercase">
                          <th className="pb-2 pr-4">Voce</th>
                          <th className="pb-2 pr-4 text-right">Quantità</th>
                          <th className="pb-2 text-right">%</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr className="font-semibold bg-slate-50">
                          <td className="py-2.5 pr-4">Quantità NC Totale</td>
                          <td className="py-2.5 pr-4 text-right font-mono">{nc.total_nonconforming_quantity}</td>
                          <td className="py-2.5 text-right">100%</td>
                        </tr>
                        {[
                          { label: 'Scarto', key: 'scrapped_quantity' },
                          { label: 'Scarto Interno', key: 'internal_scrap_quantity' },
                          { label: 'Concessione Cliente', key: 'customer_concession_quantity' },
                          { label: 'Rilavorati', key: 'reworked_quantity' },
                        ].map(row => {
                          const val = nc[row.key] || 0;
                          const pct = nc.total_nonconforming_quantity > 0 ? ((val / nc.total_nonconforming_quantity) * 100).toFixed(1) : '0.0';
                          return (
                            <tr key={row.key}>
                              <td className="py-2.5 pr-4 text-slate-700">{row.label}</td>
                              <td className="py-2.5 pr-4 text-right font-mono">{val}</td>
                              <td className="py-2.5 text-right text-slate-500">{pct}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const sum = (nc.scrapped_quantity || 0) + (nc.internal_scrap_quantity || 0) + (nc.customer_concession_quantity || 0) + (nc.reworked_quantity || 0);
                    const total = nc.total_nonconforming_quantity || 0;
                    if (total > 0 && sum !== total) {
                      return (
                        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm" data-testid="disposition-warning-readonly">
                          <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" />
                          <span>Le quantità non coincidono: {sum} di {total} pezzi assegnati (differenza: {total - sum})</span>
                        </div>
                      );
                    }
                    if (total > 0 && sum === total) {
                      return (
                        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm" data-testid="disposition-ok">
                          <span>Tutte le quantità sono state assegnate correttamente</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              ) : (
                <p className="text-sm text-slate-400 py-2" data-testid="disposition-empty">Nessuna disposizione materiale registrata</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── NC Ricevute: Esito Contestazione ─── */}
      {nc.nc_category === 'ricevuta' && (
        <Card data-testid="contestazione-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Esito Contestazione</CardTitle>
              {hasRole('admin', 'quality') && !contestazioneEditing && (
                <Button variant="outline" size="sm" onClick={() => setContestazioneEditing(true)} data-testid="edit-contestazione-btn">
                  <Edit2 className="w-4 h-4 mr-1" />Modifica
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {contestazioneEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Esito Tecnico *</Label>
                    <Select value={contestazioneForm.technical_outcome || 'none'} onValueChange={v => setContestazioneForm(f => ({ ...f, technical_outcome: v === 'none' ? '' : v }))}>
                      <SelectTrigger data-testid="technical-outcome-select"><SelectValue placeholder="Seleziona esito" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non definito</SelectItem>
                        <SelectItem value="accettata">Accettata</SelectItem>
                        <SelectItem value="parzialmente_accettata">Parzialmente accettata</SelectItem>
                        <SelectItem value="rifiutata">Rifiutata</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Data Risposta</Label>
                    <Input type="date" value={contestazioneForm.response_date} onChange={e => setContestazioneForm(f => ({ ...f, response_date: e.target.value }))} data-testid="response-date-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Motivazione Esito</Label>
                  <Textarea value={contestazioneForm.outcome_reason} onChange={e => setContestazioneForm(f => ({ ...f, outcome_reason: e.target.value }))} rows={3} data-testid="outcome-reason-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Note Risposta</Label>
                  <Textarea value={contestazioneForm.response_notes} onChange={e => setContestazioneForm(f => ({ ...f, response_notes: e.target.value }))} rows={2} data-testid="response-notes-input" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => { setContestazioneEditing(false); setContestazioneForm({ technical_outcome: nc.technical_outcome || '', outcome_reason: nc.outcome_reason || '', response_notes: nc.response_notes || '', response_date: nc.response_date || '' }); }}>Annulla</Button>
                  <Button size="sm" onClick={handleSaveContestazione} data-testid="save-contestazione-btn">Salva</Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Esito Tecnico</p>
                  {nc.technical_outcome ? (
                    <Badge className={nc.technical_outcome === 'accettata' ? 'bg-emerald-500 text-white' : nc.technical_outcome === 'rifiutata' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}>
                      {nc.technical_outcome === 'accettata' ? 'Accettata' : nc.technical_outcome === 'parzialmente_accettata' ? 'Parzialmente Accettata' : nc.technical_outcome === 'rifiutata' ? 'Rifiutata' : nc.technical_outcome}
                    </Badge>
                  ) : <p className="text-sm text-slate-400">Non definito</p>}
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Data Risposta</p>
                  <p className="text-sm">{nc.response_date || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Motivazione</p>
                  <p className="text-sm text-slate-700">{nc.outcome_reason || '-'}</p>
                </div>
                {nc.response_notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Note Risposta</p>
                    <p className="text-sm text-slate-700">{nc.response_notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── NC Ricevute: Gestione Economica ─── */}
      {nc.nc_category === 'ricevuta' && (
        <Card data-testid="economica-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Gestione Economica</CardTitle>
              {hasRole('admin', 'quality') && !economicaEditing && (
                <Button variant="outline" size="sm" onClick={() => setEconomicaEditing(true)} data-testid="edit-economica-btn">
                  <Edit2 className="w-4 h-4 mr-1" />Modifica
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {economicaEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Importo Addebito Richiesto (€)</Label>
                    <Input type="number" step="0.01" value={economicaForm.requested_charge_amount} onChange={e => setEconomicaForm(f => ({ ...f, requested_charge_amount: e.target.value }))} data-testid="requested-charge-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Importo Addebito Riconosciuto (€)</Label>
                    <Input type="number" step="0.01" value={economicaForm.recognized_charge_amount} onChange={e => setEconomicaForm(f => ({ ...f, recognized_charge_amount: e.target.value }))} data-testid="recognized-charge-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Esito Economico</Label>
                  <Select value={economicaForm.economic_outcome || 'none'} onValueChange={v => setEconomicaForm(f => ({ ...f, economic_outcome: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="economic-outcome-select"><SelectValue placeholder="Seleziona esito" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non definito</SelectItem>
                      <SelectItem value="nessun_addebito">Nessun addebito</SelectItem>
                      <SelectItem value="addebito_accettato">Addebito accettato</SelectItem>
                      <SelectItem value="addebito_parzialmente_accettato">Addebito parzialmente accettato</SelectItem>
                      <SelectItem value="addebito_rifiutato">Addebito rifiutato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Note Economiche</Label>
                  <Textarea value={economicaForm.economic_notes} onChange={e => setEconomicaForm(f => ({ ...f, economic_notes: e.target.value }))} rows={2} data-testid="economic-notes-input" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => { setEconomicaEditing(false); setEconomicaForm({ requested_charge_amount: nc.requested_charge_amount ?? '', recognized_charge_amount: nc.recognized_charge_amount ?? '', economic_outcome: nc.economic_outcome || '', economic_notes: nc.economic_notes || '' }); }}>Annulla</Button>
                  <Button size="sm" onClick={handleSaveEconomica} data-testid="save-economica-btn">Salva</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Addebito Richiesto</p>
                    <p className="text-lg font-semibold text-rose-600">{nc.requested_charge_amount != null ? `€${parseFloat(nc.requested_charge_amount).toFixed(2)}` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Addebito Riconosciuto</p>
                    <p className="text-lg font-semibold text-emerald-600">{nc.recognized_charge_amount != null ? `€${parseFloat(nc.recognized_charge_amount).toFixed(2)}` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Esito Economico</p>
                    {nc.economic_outcome ? (
                      <Badge className={nc.economic_outcome === 'nessun_addebito' ? 'bg-emerald-500 text-white' : nc.economic_outcome === 'addebito_rifiutato' ? 'bg-red-500 text-white' : nc.economic_outcome === 'addebito_accettato' ? 'bg-amber-500 text-white' : 'bg-sky-500 text-white'}>
                        {({ nessun_addebito: 'Nessun addebito', addebito_accettato: 'Addebito accettato', addebito_parzialmente_accettato: 'Addebito parz. accettato', addebito_rifiutato: 'Addebito rifiutato' })[nc.economic_outcome] || nc.economic_outcome}
                      </Badge>
                    ) : <p className="text-sm text-slate-400">Non definito</p>}
                  </div>
                  {nc.requested_charge_amount != null && nc.recognized_charge_amount != null && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Differenza</p>
                      <p className="text-lg font-semibold">{`€${(nc.requested_charge_amount - nc.recognized_charge_amount).toFixed(2)}`}</p>
                    </div>
                  )}
                </div>
                {nc.economic_notes && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Note Economiche</p>
                    <p className="text-sm text-slate-700">{nc.economic_notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── NC Ricevute: Info Specifica ─── */}
      {nc.nc_category === 'ricevuta' && (nc.external_reference || nc.sender || nc.received_date || nc.response_deadline || nc.complaint_description) && (
        <Card data-testid="nc-ricevuta-info">
          <CardHeader>
            <CardTitle className="text-base">Dettagli Reclamo Ricevuto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Rif. Esterno</p>
                <p className="text-sm font-medium">{nc.external_reference || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Mittente</p>
                <p className="text-sm">{nc.sender || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Data Ricezione</p>
                <p className="text-sm">{nc.received_date || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Deadline Risposta</p>
                <p className="text-sm">{nc.response_deadline || '-'}</p>
              </div>
              {nc.complaint_description && (
                <div className="col-span-2 sm:col-span-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Descrizione Reclamo</p>
                  <p className="text-sm text-slate-700">{nc.complaint_description}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── NC Emesse: Info Specifica ─── */}
      {nc.nc_category === 'emessa' && (nc.recipient || nc.issue_date || nc.problem_description || nc.requested_action) && (
        <Card data-testid="nc-emessa-info">
          <CardHeader>
            <CardTitle className="text-base">Dettagli NC Emessa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Destinatario</p>
                <p className="text-sm font-medium">{nc.recipient || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Data Emissione</p>
                <p className="text-sm">{nc.issue_date || '-'}</p>
              </div>
              {nc.problem_description && (
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Descrizione Problema</p>
                  <p className="text-sm text-slate-700">{nc.problem_description}</p>
                </div>
              )}
              {nc.requested_action && (
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Azione Richiesta</p>
                  <p className="text-sm text-slate-700">{nc.requested_action}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Costs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dettaglio Costi</CardTitle>
        </CardHeader>
        <CardContent>
          {costs.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No costs recorded</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit Cost</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {costs.map((cost) => (
                    <tr key={cost.id}>
                      <td className="capitalize">{cost.cost_category?.replace('_', ' ')}</td>
                      <td>{cost.cost_description}</td>
                      <td>{cost.quantity || '-'}</td>
                      <td>{cost.unit_cost ? `€${cost.unit_cost.toFixed(2)}` : '-'}</td>
                      <td className="font-medium">€{cost.total_cost.toFixed(2)}</td>
                      <td>
                        {hasRole('admin', 'quality') && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCost(cost.id)}>
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {nc.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">{nc.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Attachments - Defect Photos, CMM Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Allegati</CardTitle>
        </CardHeader>
        <CardContent>
          <FileUploadZone
            entityType="nc"
            entityId={id}
            attachments={attachments}
            onUploadComplete={loadAttachments}
            onDeleteComplete={loadAttachments}
            canEdit={hasRole('admin', 'quality')}
          />
        </CardContent>
      </Card>

      {/* Add Cost Dialog */}
      <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Cost</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCost}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={costForm.cost_category}
                  onValueChange={(value) => setCostForm({ ...costForm, cost_category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COST_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  value={costForm.cost_description}
                  onChange={(e) => setCostForm({ ...costForm, cost_description: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={costForm.quantity}
                    onChange={(e) => setCostForm({ ...costForm, quantity: e.target.value })}
                    onBlur={calculateTotalFromQty}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Cost (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={costForm.unit_cost}
                    onChange={(e) => setCostForm({ ...costForm, unit_cost: e.target.value })}
                    onBlur={calculateTotalFromQty}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Cost (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={costForm.total_cost}
                    onChange={(e) => setCostForm({ ...costForm, total_cost: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={costForm.notes}
                  onChange={(e) => setCostForm({ ...costForm, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCostDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Add Cost</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change NC Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Non Conformità</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateNC}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Containment Action</Label>
                <Textarea
                  value={editForm.containment_action}
                  onChange={(e) => setEditForm({ ...editForm, containment_action: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Corrective Action</Label>
                <Textarea
                  value={editForm.corrective_action}
                  onChange={(e) => setEditForm({ ...editForm, corrective_action: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Responsible Person</Label>
                  <Input
                    value={editForm.responsible_person}
                    onChange={(e) => setEditForm({ ...editForm, responsible_person: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Linked Debit Notes Section */}
      <Card data-testid="linked-debit-notes-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Note di Debito Collegate
          </CardTitle>
          <Button size="sm" onClick={openDebitNoteDialog} data-testid="add-debit-note-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Genera
          </Button>
        </CardHeader>
        <CardContent>
          {debitNotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Numero</th>
                    <th>Data</th>
                    <th>Destinatario</th>
                    <th>Oggetto</th>
                    <th className="text-right">Importo</th>
                    <th>Stato</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {debitNotes.map(d => (
                    <tr key={d.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/debit-notes/${d.id}`)} data-testid={`debit-note-row-${d.id}`}>
                      <td className="font-mono font-medium">{d.debit_note_number}</td>
                      <td className="text-sm">{new Date(d.issue_date).toLocaleDateString('it-IT')}</td>
                      <td className="text-sm">{d.customer_name || d.supplier_name || '-'}</td>
                      <td className="text-sm">{d.subject}</td>
                      <td className="text-right font-semibold">{new Intl.NumberFormat('it-IT', { style: 'currency', currency: d.currency || 'EUR' }).format(d.total_amount || 0)}</td>
                      <td>
                        <Badge className={d.status === 'bozza' ? 'bg-slate-100 text-slate-700' : d.status === 'emessa' ? 'bg-blue-100 text-blue-800' : d.status === 'inviata' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}>
                          {d.status === 'bozza' ? 'Bozza' : d.status === 'emessa' ? 'Emessa' : d.status === 'inviata' ? 'Inviata' : 'Chiusa'}
                        </Badge>
                      </td>
                      <td><ExternalLink className="w-3.5 h-3.5 text-slate-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">Nessuna nota di debito collegata a questa NC.</p>
          )}
        </CardContent>
      </Card>

      {/* Create Debit Note Dialog */}
      <Dialog open={dnDialogOpen} onOpenChange={setDnDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Genera Nota di Debito</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDebitNote}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Tipo Destinatario *</Label>
                <Select value={dnForm.recipient_type} onValueChange={v => setDnForm(f => ({ ...f, recipient_type: v }))}>
                  <SelectTrigger data-testid="dn-recipient-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Cliente</SelectItem>
                    <SelectItem value="supplier">Fornitore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {dnForm.recipient_type === 'customer' ? (
                <div>
                  <Label>Cliente *</Label>
                  <select value={dnForm.customer_id} onChange={e => setDnForm(f => ({ ...f, customer_id: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="dn-customer">
                    <option value="">Seleziona cliente...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{`${c.code} - ${c.name}`}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <Label>Fornitore *</Label>
                  <select value={dnForm.supplier_id} onChange={e => setDnForm(f => ({ ...f, supplier_id: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="dn-supplier">
                    <option value="">Seleziona fornitore...</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{`${s.code} - ${s.name}`}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label>Oggetto *</Label>
                <Input value={dnForm.subject} onChange={e => setDnForm(f => ({ ...f, subject: e.target.value }))} data-testid="dn-subject" />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea value={dnForm.description} onChange={e => setDnForm(f => ({ ...f, description: e.target.value }))} rows={3} data-testid="dn-description" />
              </div>
              <div>
                <Label>Note</Label>
                <Textarea value={dnForm.notes} onChange={e => setDnForm(f => ({ ...f, notes: e.target.value }))} rows={2} data-testid="dn-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDnDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="create-dn-submit">Crea Nota di Debito</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
